import React from 'react';
import {DistributionRecord} from '../models/distribution.model';
import AppTable, {TableColumn} from '../../../Presentation/components/ui/AppTable';
import AppTableHeaderCell from '../../../Presentation/components/ui/AppTableHeaderCell';
import {AppText, AppCaption, AppTitle, AppCardTitle, AppSectionTitle, AppBodyText, AppSmallText, AppLabelText} from '../../../Presentation/components/ui/Typography';
import {Package, User, Calendar, DollarSign, Hash, CheckCircle, X, Eye, Trash2} from 'lucide-react';
import Modal from '../../../Presentation/components/ui/Modal';
import Card from '../../../Presentation/components/ui/Card';
import DateTimeDisplay from '../../../Presentation/components/ui/DateTimeDisplay';
import {useTableExport, useExportConfig} from '../../../Presentation/hooks/useTableExport';
import ExportButton from '../../../Presentation/components/ui/ExportButton';
import {ExportFormat} from '../../../types/export.types';
import {ExportFormatters} from '../../../Presentation/utils/exportFormatters';
import {useDistributionUIStore} from '../store/distributionUIStore';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';
import {formatCurrency} from '../../../Presentation/utils/numberUtils';

interface DistributionTableProps {
  records: DistributionRecord[];
  pageSize?: number;
  currentPage?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onDeleteTransaction?: (transactionId: string) => Promise<boolean>;
  exportable?: boolean;
  loading?: boolean;
}

const DistributionTable: React.FC<DistributionTableProps> = ({
  records,
  pageSize = 20,
  currentPage = 1,
  totalCount,
  onPageChange,
  onPageSizeChange,
  onDeleteTransaction,
  exportable = false,
  loading = false
}) => {
  const {hasPermission} = useAuthorization();
  const {
    selectedRecord, isDetailsModalOpen: isModalOpen,
    openDetailsModal, closeDetailsModal
  } = useDistributionUIStore();

  // Export configuration
  const {createConfig} = useExportConfig<DistributionRecord>();

  const exportConfig = createConfig([
    {key: 'id', title: 'معرف التوزيع'},
    {key: 'marketerName', title: 'اسم المسوق'},
    {key: 'productName', title: 'اسم المنتج'},
    {key: 'productBarcode', title: 'باركود المنتج'},
    {
      key: 'quantity',
      title: 'الكمية',
      formatter: (value, record) => {
        if (record.hasSerial && record.serials && record.serials.length > 0) {
          return `${record.serials.length} (سيريال)`;
        }
        return `${value} (قطعة)`;
      }
    },
    {key: 'hasSerial', title: 'نوع المنتج', formatter: ExportFormatters.serialType},
    {key: 'notes', title: 'نوع العملية'},
    {key: 'deliveredAt', title: 'تاريخ التسليم', formatter: ExportFormatters.dateTime},
    {key: 'cost', title: 'سعر الوحدة', formatter: ExportFormatters.currencySARExport},
    {key: 'deliveredByName', title: 'تم التسليم بواسطة'},
    {key: 'status', title: 'الحالة', formatter: ExportFormatters.distributionStatus},
    {
      key: 'serials',
      title: 'الأرقام التسلسلية',
      formatter: (_value, record) => {
        if (record.hasSerial && record.serials && record.serials.length > 0) {
          return record.serials.join(', ');
        }
        return 'لا يوجد';
      }
    }
  ], 'توزيع', 'تصدير سجلات التوزيع', 'تصدير جميع سجلات التوزيع مع التفاصيل الكاملة');

  // Export hook
  const {exportData, isExporting, canExport, recordCount} = useTableExport({
    data: records,
    config: exportConfig
  });

  // Handle export
  const handleExport = async (format: ExportFormat) => {
    await exportData(format);
  };

  const handleRowClick = (record: DistributionRecord) => {
    openDetailsModal(record);
  };

  const closeModal = () => {
    closeDetailsModal();
  };

  const handleViewDetails = (record: DistributionRecord) => {
    openDetailsModal(record);
  };

  const handleDeleteTransaction = async (record: DistributionRecord) => {
    if (onDeleteTransaction) {
      try {
        const success = await onDeleteTransaction(record.id);
        if (success) {
          // Success notification is handled in the hook
        }
      } catch (error) {
        // Error notification is handled in the hook
      }
    }
  };

  const columns: TableColumn<DistributionRecord>[] = [
    {
      key: 'id',
      title: <AppTableHeaderCell label="المعرف" />,
      render: (record: DistributionRecord) => (
        <AppText className="font-mono text-right">{record.id}</AppText>
      )
    },
    {
      key: 'marketerName',
      title: <AppTableHeaderCell label="المسوق" />,
      render: (record: DistributionRecord) => (
        <div className="text-right">
          <AppText className="font-medium">{record.marketerName}</AppText>
        </div>
      )
    },
    {
      key: 'productName',
      title: <AppTableHeaderCell label="المنتج" />,
      render: (record: DistributionRecord) => (
        <div className="space-y-1 text-right">
          <AppText className="font-medium">{record.productName}</AppText>
          {record.productBarcode && (
            <AppCaption className="text-gray-500 font-mono block">{record.productBarcode}</AppCaption>
          )}
        </div>
      )
    },
    {
      key: 'quantity',
      title: <AppTableHeaderCell label="الكمية" />,
      render: (record: DistributionRecord) => (
        <div className="text-center space-y-1">
          <AppCardTitle>
            {record.hasSerial && record.serials && record.serials.length > 0
              ? record.serials.length
              : (record.quantity || 0)
            }
          </AppCardTitle>
          <AppCaption className={`block ${record.hasSerial ? 'text-brand-600' : 'text-gray-500'}`}>
            {record.hasSerial ? 'سيريال' : 'بدون سيريال'}
          </AppCaption>
        </div>
      )
    },

    {
      key: 'transactionType',
      title: <AppTableHeaderCell label="نوع العملية" />,
      render: (record: DistributionRecord) => (
        <div className="text-center">
          <span className="px-3 py-1 bg-brand-100 text-brand-800 rounded-full font-medium">
            {record.notes}
          </span>
        </div>
      )
    },
    {
      key: 'deliveredAt',
      title: <AppTableHeaderCell label="تاريخ التسليم" />,
      render: (record: DistributionRecord) => (
        <DateTimeDisplay dateString={record.deliveredAt} />
      )
    },
    {
      key: 'cost',
      title: <AppTableHeaderCell label="سعر الوحدة" />,
      render: (record: DistributionRecord) => (
        <div className="text-right">
          <AppSectionTitle className="font-bold text-green-600">
            {formatCurrency(record.cost || 0)}
          </AppSectionTitle>
        </div>
      )
    },
    {
      key: 'deliveredBy',
      title: <AppTableHeaderCell label="تم التسليم بواسطة" />,
      render: (record: DistributionRecord) => (
        <AppBodyText className="text-right">{record.deliveredByName}</AppBodyText>
      )
    }
  ];


  return (
    <>
      {/* Export Button */}
      {exportable && (
        <div className="mb-4 flex justify-end">
          <ExportButton
            onExport={handleExport}
            isExporting={isExporting}
            canExport={canExport}
            recordCount={recordCount}
            showDropdown={true}
          />
        </div>
      )}

      <AppTable
        columns={columns}
        data={records}
        rowKey={(record: DistributionRecord, index?: number) => `dist-${record.id}-${index || 0}`}
        emptyText="لا توجد سجلات توزيع"
        page={currentPage}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onRowClick={handleRowClick}
        loading={loading}
        loadingRows={pageSize || 20}
        loadingMessage="جاري تحميل سجلات التوزيع..."
        actions={(record) => {
          const actions = [];

          // View Details - Requires distribution read permission
          if (hasPermission(PERMISSIONS.READ_DISTRIBUTION)) {
            actions.push({
              label: 'عرض التفاصيل',
              icon: <Eye className="text-brand-600" size={20} />,
              onClick: () => handleViewDetails(record)
            });
          }

          // Delete Transaction - Requires distribution delete permission
          if (onDeleteTransaction && hasPermission(PERMISSIONS.DELETE_DISTRIBUTION)) {
            actions.push({
              label: 'حذف المعاملة',
              icon: <Trash2 className="text-red-600" size={20} />,
              onClick: () => handleDeleteTransaction(record),
              danger: true
            });
          }

          return actions;
        }}
      />

      {/* Details Modal */}
      <Modal
        open={isModalOpen}
        onClose={closeModal}
        title={`تفاصيل التوزيع #${selectedRecord?.id || ''}`}
        width="max-w-4xl"
      >
        {selectedRecord && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand-100 rounded-lg">
                  <Hash className="h-6 w-6 text-brand-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">توزيع #{selectedRecord.id}</h3>
                </div>
              </div>
              <div className="text-right">
                <span className="text-sm text-gray-600">نوع العملية:</span>
                <div className="text-lg font-semibold text-brand-600">{selectedRecord.notes}</div>
              </div>
            </div>

            {/* Transaction Type */}
            <Card className="p-4 bg-brand-50 border-brand-200">
              <div className="flex items-center gap-2 mb-3">
                <Hash className="h-5 w-5 text-brand-600" />
                <h4 className="font-semibold text-brand-800">نوع العملية</h4>
              </div>
              <div className="text-center">
                <AppCardTitle className="font-bold text-brand-600">{selectedRecord.notes}</AppCardTitle>
                <AppBodyText className="text-brand-700 mt-1">عملية توزيع منتجات</AppBodyText>
              </div>
            </Card>

            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Marketer Information */}
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <User className="h-5 w-5 text-brand-600" />
                  <h4 className="font-semibold">معلومات المسوق</h4>
                </div>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-gray-600">الاسم:</span>
                    <AppText className="font-medium">{selectedRecord.marketerName}</AppText>
                  </div>
                </div>
              </Card>

              {/* Product Information */}
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Package className="h-5 w-5 text-green-600" />
                  <h4 className="font-semibold">معلومات المنتج</h4>
                </div>
                <div className="space-y-3">
                  {/* Product Image */}
                  {selectedRecord.productImages && selectedRecord.productImages.length > 0 ? (
                    <div className="flex justify-center">
                      <img
                        src={selectedRecord.productImages[0]}
                        alt={selectedRecord.productName}
                        className="w-24 h-24 object-cover rounded-lg border border-green-200"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="flex justify-center">
                      <div className="w-24 h-24 bg-gray-200 rounded-lg border border-green-200 flex items-center justify-center">
                        <Package className="w-8 h-8 text-gray-400" />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <div>
                      <span className="text-sm text-gray-600">الاسم:</span>
                      <AppText className="font-medium">{selectedRecord.productName}</AppText>
                    </div>
                    {selectedRecord.productBarcode && (
                      <div>
                        <span className="text-sm text-gray-600">الباركود:</span>
                        <AppText className="font-mono">{selectedRecord.productBarcode}</AppText>
                      </div>
                    )}
                    <div>
                      <span className="text-sm text-gray-600">الكمية:</span>
                      <AppSectionTitle className="font-bold">
                        {selectedRecord.hasSerial && selectedRecord.serials && selectedRecord.serials.length > 0
                          ? selectedRecord.serials.length
                          : selectedRecord.quantity
                        }
                      </AppSectionTitle>
                      {selectedRecord.hasSerial && selectedRecord.serials && selectedRecord.serials.length > 0 && (
                        <AppSmallText className="text-brand-600">(عدد السيريالات)</AppSmallText>
                      )}
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">النوع:</span>
                      <AppText className={selectedRecord.hasSerial ? "text-brand-600" : "text-gray-600"}>
                        {selectedRecord.hasSerial ? "بسيريال" : "بدون سيريال"}
                      </AppText>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Delivery Information */}
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="h-5 w-5 text-purple-600" />
                  <h4 className="font-semibold">معلومات التسليم</h4>
                </div>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-gray-600">التاريخ والوقت:</span>
                    <DateTimeDisplay dateString={selectedRecord.deliveredAt} dateClassName="font-medium" />
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">تم التسليم بواسطة:</span>
                    <AppText className="font-medium">{selectedRecord.deliveredByName}</AppText>
                  </div>
                </div>
              </Card>

              {/* Cost Information */}
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="h-5 w-5 text-green-600" />
                  <h4 className="font-semibold">معلومات التكلفة</h4>
                </div>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-gray-600">سعر الوحدة:</span>
                    <AppText className="font-medium text-gray-700">
                      {formatCurrency(selectedRecord.cost || 0)}
                    </AppText>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">الكمية:</span>
                    <AppText className="font-medium text-gray-700">
                      {(() => {
                        const actualQuantity = selectedRecord.hasSerial && selectedRecord.serials && selectedRecord.serials.length > 0
                          ? selectedRecord.serials.length
                          : selectedRecord.quantity;
                        return `${actualQuantity} وحدة`;
                      })()}
                    </AppText>
                  </div>
                  <div className="pt-2 border-t">
                    <span className="text-sm text-gray-600">التكلفة الإجمالية:</span>
                    <AppCardTitle className="font-bold text-green-600">
                      {(() => {
                        const actualQuantity = selectedRecord.hasSerial && selectedRecord.serials && selectedRecord.serials.length > 0
                          ? selectedRecord.serials.length
                          : selectedRecord.quantity;
                        return formatCurrency((selectedRecord.cost || 0) * actualQuantity);
                      })()}
                    </AppCardTitle>
                  </div>
                </div>
              </Card>
            </div>

            {/* Serials Section */}
            {selectedRecord.hasSerial && selectedRecord.serials && selectedRecord.serials.length > 0 && (
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-brand-600" />
                  <h4 className="font-semibold">الأرقام التسلسلية</h4>
                  <span className="text-sm text-gray-500">({selectedRecord.serials.length} سيريال)</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {selectedRecord.serials.map((serial, index) => (
                    <div key={index} className="p-2 bg-gray-50 rounded border text-center hover:bg-gray-100 transition-colors">
                      <AppBodyText className="font-mono">{serial}</AppBodyText>
                    </div>
                  ))}
                </div>
              </Card>
            )}


            {/* Close Button */}
            <div className="flex justify-end pt-4 border-t">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                إغلاق
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default DistributionTable;
